import { useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  PILLARS,
  PILLAR_COLORS,
  SECTOR_COLORS,
  pct,
  type Pillar,
  type PillarRank,
  type PillarRow,
} from "../../lib/pillars";
import { ACCENT, AXIS, Card, DIM, GRID, Pick, TOOLTIP } from "./chartkit";

const RADAR_COLORS = ["#ffca28", "#26a69a", "#66bb6a", "#4c8dff", "#ef5350", "#ba68c8", "#ff8a65", "#4dd0e1"];
const MAX_POINTS = 1500;

/**
 * Four views of the same ranking: what the top names are made of, how two pillars
 * trade off across the universe, which industries the top of the list leans into,
 * and how each industry scores on a pillar.
 */
export function ChartsView({ data, selected }: { data: PillarRank; selected: number[] }) {
  const [x, setX] = useState<Pillar>("value");
  const [y, setY] = useState<Pillar>("quality");
  const [by, setBy] = useState<Pillar>("risk");

  const radarRows = useMemo(() => {
    const picked = data.rows.filter((r) => selected.includes(r.cik));
    return picked.length ? picked : data.rows.slice(0, 8);
  }, [data, selected]);
  const radar = useMemo(
    () =>
      PILLARS.map((p) => ({
        pillar: p,
        ...Object.fromEntries(radarRows.map((r) => [r.ticker, r[p] ?? 0])),
      })),
    [radarRows],
  );

  const points = useMemo(() => {
    const groups = new Map<string, PillarRow[]>();
    for (const r of data.rows.slice(0, MAX_POINTS)) {
      if (r[x] == null || r[y] == null) continue;
      const g = groups.get(r.ff12);
      if (g) g.push(r);
      else groups.set(r.ff12, [r]);
    }
    return [...groups.entries()].sort((a, b) => b[1].length - a[1].length);
  }, [data, x, y]);

  const bySector = useMemo(
    () =>
      data.sectors
        .filter((s) => s[by] != null)
        .map((s) => ({ ff12: s.ff12, mean: s[by] as number, n: s.n }))
        .sort((a, b) => b.mean - a.mean),
    [data, by],
  );

  return (
    <div className="p-3 grid gap-3 grid-cols-1 xl:grid-cols-2">
      <Card
        title={`Pillar radar — ${selected.length ? "selected names" : "top 8"}`}
        subtitle="Each pillar as a percentile of the investable universe. A pillar a name has no data for is drawn at the centre."
        height={340}
      >
        <ResponsiveContainer>
          <RadarChart data={radar} outerRadius="72%">
            <PolarGrid stroke={GRID} />
            <PolarAngleAxis dataKey="pillar" tick={AXIS} />
            <PolarRadiusAxis domain={[0, 1]} tick={false} axisLine={false} />
            {radarRows.map((r, i) => (
              <Radar
                key={r.cik}
                dataKey={r.ticker}
                stroke={RADAR_COLORS[i % RADAR_COLORS.length]}
                fill={RADAR_COLORS[i % RADAR_COLORS.length]}
                fillOpacity={0.08}
                isAnimationActive={false}
              />
            ))}
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <Tooltip {...TOOLTIP} formatter={(v: number) => pct(v, 0)} />
          </RadarChart>
        </ResponsiveContainer>
      </Card>

      <Card
        title={`${y} vs ${x}`}
        subtitle={`One dot per company, coloured by industry${
          data.n_ranked > MAX_POINTS ? ` (top ${MAX_POINTS} by rank shown)` : ""
        }.`}
        height={340}
        right={
          <div className="flex items-center gap-1 text-[10px] text-term-dim">
            x <Pick value={x} options={PILLARS} onChange={setX} /> y{" "}
            <Pick value={y} options={PILLARS} onChange={setY} />
          </div>
        }
      >
        <ResponsiveContainer>
          <ScatterChart margin={{ top: 8, right: 12, bottom: 4, left: -18 }}>
            <CartesianGrid stroke={GRID} />
            <XAxis type="number" dataKey={x} domain={[0, 1]} tick={AXIS} name={x} />
            <YAxis type="number" dataKey={y} domain={[0, 1]} tick={AXIS} name={y} />
            <Tooltip
              {...TOOLTIP}
              cursor={{ stroke: GRID }}
              content={({ payload }) => {
                const r = payload?.[0]?.payload as PillarRow | undefined;
                if (!r) return null;
                return (
                  <div style={TOOLTIP.contentStyle} className="px-2 py-1">
                    <div className="text-term-accent">
                      {r.ticker} <span className="text-term-dim">#{r.rank}</span>
                    </div>
                    <div className="text-term-dim">{r.title}</div>
                    <div>
                      {x} {pct(r[x], 0)} · {y} {pct(r[y], 0)} · {r.ff12}
                    </div>
                  </div>
                );
              }}
            />
            {points.map(([sector, rows]) => (
              <Scatter
                key={sector}
                name={sector}
                data={rows}
                fill={SECTOR_COLORS[sector] ?? DIM}
                fillOpacity={0.7}
                isAnimationActive={false}
              />
            ))}
            <Legend wrapperStyle={{ fontSize: 10 }} />
          </ScatterChart>
        </ResponsiveContainer>
      </Card>

      <Card
        title="Sector tilt"
        subtitle={`Share of the top ${data.n_top.toLocaleString()} names against share of all ${data.n_ranked.toLocaleString()}. Where the bars differ, the ranking is making an industry bet.`}
        height={360}
      >
        <ResponsiveContainer>
          <BarChart data={data.sectors} layout="vertical" margin={{ top: 4, right: 12, bottom: 0, left: 4 }}>
            <CartesianGrid stroke={GRID} horizontal={false} />
            <XAxis type="number" tick={AXIS} tickFormatter={(v: number) => pct(v, 0)} />
            <YAxis type="category" dataKey="ff12" tick={AXIS} width={44} />
            <Tooltip {...TOOLTIP} cursor={{ fill: GRID }} formatter={(v: number) => pct(v)} />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <Bar dataKey="share_universe" name="Universe" fill={DIM} isAnimationActive={false} />
            <Bar dataKey="share_top" name="Top quintile" fill={ACCENT} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <Card
        title={`Mean ${by} score by sector`}
        subtitle="Average percentile of the names in each industry. 50% is the universe; higher is better on every pillar, so a high risk score means SAFER."
        height={360}
        right={<Pick value={by} options={PILLARS} onChange={setBy} />}
      >
        <ResponsiveContainer>
          <BarChart data={bySector} layout="vertical" margin={{ top: 4, right: 12, bottom: 0, left: 4 }}>
            <CartesianGrid stroke={GRID} horizontal={false} />
            <XAxis type="number" domain={[0, 1]} tick={AXIS} tickFormatter={(v: number) => pct(v, 0)} />
            <YAxis type="category" dataKey="ff12" tick={AXIS} width={44} />
            <Tooltip {...TOOLTIP} cursor={{ fill: GRID }} formatter={(v: number) => pct(v)} />
            <ReferenceLine x={0.5} stroke={DIM} strokeDasharray="3 3" />
            <Bar dataKey="mean" name={`mean ${by}`} fill={PILLAR_COLORS[by]} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
}
